import { getSupabase, isSupabaseConfigured } from '../lib/supabase';
import { assertOnlineOrThrow } from '../lib/offlineGuard';
import { searchUsers } from './friendshipsApi';

function escapeIlike(s) {
  return String(s || '').replace(/[%_\\]/g, '\\$&');
}

function mergeBySlug(rows, slugKey = 'slug') {
  const m = new Map();
  (rows || []).forEach((r) => {
    const id = r?.[slugKey];
    if (id && !m.has(id)) m.set(id, r);
  });
  return [...m.values()];
}

async function searchProductsIlike(sb, pat) {
  const { data, error } = await sb
    .from('products')
    .select('slug,title,price_display,location,time_display,created_at,description')
    .eq('is_published', true)
    .or(`title.ilike.${pat},description.ilike.${pat}`)
    .order('created_at', { ascending: false })
    .limit(40);
  if (error) throw error;
  return data || [];
}

async function searchProductsFts(sb, term) {
  const q = term
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.replace(/['&|!():]/g, ''))
    .join(' & ');
  if (!q) return [];
  const { data, error } = await sb
    .from('products')
    .select('slug,title,price_display,location,time_display,created_at,description')
    .eq('is_published', true)
    .textSearch('search_fts', q, { type: 'plain', config: 'simple' })
    .limit(40);
  if (error) return [];
  return data || [];
}

async function searchPostsIlike(sb, pat) {
  const { data, error } = await sb
    .from('posts')
    .select('slug,title,preview,location,time_display,created_at,body')
    .eq('is_published', true)
    .or(`title.ilike.${pat},preview.ilike.${pat},body.ilike.${pat}`)
    .order('created_at', { ascending: false })
    .limit(40);
  if (error) throw error;
  return data || [];
}

async function searchPostsFts(sb, term) {
  const q = term
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.replace(/['&|!():]/g, ''))
    .join(' & ');
  if (!q) return [];
  const { data, error } = await sb
    .from('posts')
    .select('slug,title,preview,location,time_display,created_at,body')
    .eq('is_published', true)
    .textSearch('search_fts', q, { type: 'plain', config: 'simple' })
    .limit(40);
  if (error) return [];
  return data || [];
}

async function searchJobsIlike(sb, pat) {
  const { data, error } = await sb
    .from('jobs')
    .select('id,type,title,description,salary,region,language,contact,created_at')
    .eq('active', true)
    .or(`title.ilike.${pat},description.ilike.${pat},region.ilike.${pat},salary.ilike.${pat}`)
    .order('created_at', { ascending: false })
    .limit(40);
  if (error) throw error;
  return data || [];
}

async function searchJobsFts(sb, term) {
  const q = term
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.replace(/['&|!():]/g, ''))
    .join(' & ');
  if (!q) return [];
  const { data, error } = await sb
    .from('jobs')
    .select('id,type,title,description,salary,region,language,contact,created_at')
    .eq('active', true)
    .textSearch('search_fts', q, { type: 'plain', config: 'simple' })
    .limit(40);
  if (error) return [];
  return data || [];
}

function mergeJobsById(rows) {
  const m = new Map();
  (rows || []).forEach((r) => {
    const id = r?.id;
    if (id && !m.has(id)) m.set(id, r);
  });
  return [...m.values()];
}

/**
 * @param {string} q raw search string
 * @returns {Promise<{ products: object[], posts: object[], jobs: object[] }>}
 */
export async function searchProductsPostsJobs(q) {
  const term = String(q || '').trim();
  if (!term || !isSupabaseConfigured) return { products: [], posts: [], jobs: [] };
  const sb = getSupabase();
  if (!sb) return { products: [], posts: [], jobs: [] };
  assertOnlineOrThrow();
  const safe = escapeIlike(term).replace(/,/g, ' ');
  const pat = `%${safe}%`;

  const [ftsPr, ftsPo, ftsJb, ilPr, ilPo, ilJb] = await Promise.all([
    searchProductsFts(sb, term),
    searchPostsFts(sb, term),
    searchJobsFts(sb, term),
    searchProductsIlike(sb, pat),
    searchPostsIlike(sb, pat),
    searchJobsIlike(sb, pat),
  ]);

  const products = mergeBySlug([...ftsPr, ...ilPr]);
  const posts = mergeBySlug([...ftsPo, ...ilPo]);
  const jobs = mergeJobsById([...ftsJb, ...ilJb]);

  return { products, posts, jobs };
}

function ftsPlainQuery(term) {
  const q = String(term || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.replace(/['&|!():]/g, ''))
    .join(' & ');
  return q || null;
}

// 카탈로그 상품 검색 (투어/서비스/숙소/부동산/메뉴 등 catalog_items 전체 — 예약형 상품).
// catalog_items.search_vector 인덱스는 이미 있었지만 검색 화면 어디에도 연결이 안 되어 있어서
// 유저가 상단 검색창으로 "마사지" 같은 상품명을 찾을 수 없던 문제를 해결.
export const searchCatalogItems = async (term, { limit = 20 } = {}) => {
  if (!isSupabaseConfigured) return [];
  const sb = getSupabase();
  if (!sb) return [];
  const raw = String(term || '').trim();
  if (!raw) return [];

  const CATALOG_COLUMNS =
    'id, title, short_description, description, thumbnail_url, base_price, list_price, currency_code, item_type, rating_avg, review_count, business_id, businesses(name)';

  const ftsQ = ftsPlainQuery(raw);
  let byFts = [];
  if (ftsQ) {
    const { data, error } = await sb
      .from('catalog_items')
      .select(CATALOG_COLUMNS)
      .eq('status', 'published')
      .eq('visibility', 'public')
      .is('deleted_at', null)
      .textSearch('search_vector', ftsQ, { type: 'plain', config: 'simple' })
      .limit(limit);
    if (!error) byFts = data ?? [];
  }

  const safe = escapeIlike(raw).replace(/,/g, ' ');
  const pat = `%${safe}%`;
  const { data: byIlike, error: ilikeError } = await sb
    .from('catalog_items')
    .select(CATALOG_COLUMNS)
    .eq('status', 'published')
    .eq('visibility', 'public')
    .is('deleted_at', null)
    .or(`title.ilike.${pat},short_description.ilike.${pat}`)
    .limit(limit);

  const merged = mergeBySlug([...byFts, ...(ilikeError ? [] : byIlike ?? [])], 'id');
  return merged.slice(0, limit);
};

// 업체 검색 (RPC 기반으로 업그레이드)
export const searchBusinesses = async (term, { region = null, limit = 20, category = null, countryCode = null } = {}) => {
  if (!isSupabaseConfigured) return [];
  const sb = getSupabase();
  if (!sb) return [];

  const q = String(term || '').trim();
  if (!q) return [];

  // 1순위: search_businesses RPC (랭킹 포함)
  try {
    const { data, error } = await sb.rpc('search_businesses', {
      p_query: q,
      p_country_code: countryCode || null,
      p_category: category || null,
      p_limit: limit,
      p_offset: 0,
    });
    if (!error && data?.length) return data;
  } catch {
    // Ignore RPC errors and fallback to direct FTS query.
  }

  // 2순위: tsvector 직접 조회 폴백
  const ftsQ = ftsPlainQuery(q);
  if (!ftsQ) return [];
  let query = sb
    .from('businesses')
    .select('id, name, category, region, images, rating, address, description, is_verified, review_count')
    .eq('status', 'approved')
    .eq('is_visible', true)
    .is('deleted_at', null)
    .textSearch('search_fts', ftsQ, {
      type: 'plain',
      config: 'simple',
    })
    .limit(limit);
  if (region) query = query.eq('region', region);
  if (category) query = query.eq('category', category);
  if (countryCode) query = query.eq('country_code', countryCode);
  const { data, error } = await query;
  if (error) return [];
  return data ?? [];
};

// 쇼츠/스토리 검색 — 제목/내용(전문검색) + 작성자 닉네임/아이디 매칭을 합쳐서 반환
export const searchStories = async (term, { limit = 10 } = {}) => {
  if (!isSupabaseConfigured) return [];
  const sb = getSupabase();
  if (!sb) return [];
  const raw = String(term || '').trim();
  if (!raw) return [];

  const STORY_COLUMNS =
    'id, title, caption, thumb_url, region, view_count, like_count, content_type, author_user_id';

  let byContent = [];
  const ftsQ = ftsPlainQuery(term);
  if (ftsQ) {
    const { data, error } = await sb
      .from('mingo_stories')
      .select(STORY_COLUMNS)
      .eq('is_active', true)
      .textSearch('search_fts', ftsQ, { type: 'plain', config: 'simple' })
      .limit(limit);
    if (!error) byContent = data ?? [];
  }

  // 작성자 닉네임/아이디로 검색 — 매칭된 작성자의 공개 스토리를 함께 노출
  let byAuthor = [];
  try {
    const matchedUsers = await searchUsers(raw, 5);
    const authorMap = new Map(matchedUsers.map((u) => [u.user_id, u.nickname]));
    const authorIds = [...authorMap.keys()];
    if (authorIds.length) {
      const { data, error } = await sb
        .from('mingo_stories')
        .select(STORY_COLUMNS)
        .eq('is_active', true)
        .eq('visibility', 'public')
        .in('author_user_id', authorIds)
        .order('created_at', { ascending: false })
        .limit(limit);
      if (!error) {
        byAuthor = (data ?? []).map((s) => ({
          ...s,
          author_nickname: authorMap.get(s.author_user_id) || null,
        }));
      }
    }
  } catch {
    /* 오프라인 등 — 작성자 검색은 best-effort */
  }

  const merged = [...byContent];
  const seenIds = new Set(merged.map((s) => s.id));
  for (const s of byAuthor) {
    if (!seenIds.has(s.id)) {
      merged.push(s);
      seenIds.add(s.id);
    }
  }
  return merged.slice(0, limit + 5);
};

// 시리즈/웹툰 검색
export const searchSeries = async (term, { limit = 10 } = {}) => {
  if (!isSupabaseConfigured) return [];
  const sb = getSupabase();
  if (!sb) return [];
  const raw = String(term || '').trim();
  if (!raw) return [];
  const safe = escapeIlike(raw).replace(/,/g, ' ');
  const pat = `%${safe}%`;
  const { data, error } = await sb
    .from('story_series')
    .select('*')
    .eq('is_active', true)
    .or(`title.ilike.${pat},description.ilike.${pat}`)
    .limit(limit);
  if (error) return [];
  return data ?? [];
};

// 통합 검색
export const searchAll = async (term, { region = null, category = null } = {}) => {
  const q = String(term ?? '').trim();
  if (!q) {
    return {
      businesses: [],
      stories: [],
      series: [],
      products: [],
      posts: [],
    };
  }

  const [businesses, stories, series] = await Promise.all([
    searchBusinesses(q, { region, category }),
    searchStories(q),
    searchSeries(q),
  ]);

  return { businesses, stories, series };
};

/** @deprecated use searchProductsPostsJobs */
export async function searchProductsAndPosts(q) {
  const r = await searchProductsPostsJobs(q);
  return { products: r.products, posts: r.posts };
}
